import { useEffect, useRef } from 'react';
import { useAuth } from '@/store/auth';
import { hubFetch } from '@/lib/hubUrl';
import { log } from '@/lib/logger';

const POLL_MS = 5000;

interface HubAuditEntry {
  id: number;
  ts_ms: number;
  level: 'info' | 'warn' | 'error';
  kind: string;
  message: string;
  actor: string | null;
  data: Record<string, unknown> | null;
}

/**
 * Polls the Hub's `/audit` endpoint for recent server-side events and mirrors
 * them into the client logger so both sides land in the same timeline.
 * Tracks the highest seen audit id locally so we only fetch new entries.
 */
export function useHubAuditPoll() {
  const token = useAuth((s) => s.token);
  const sinceRef = useRef<number>(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let timer: number | null = null;

    async function poll() {
      if (cancelled) return;
      try {
        const url = `/audit?since=${sinceRef.current}`;
        const resp = await hubFetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const entries = (await resp.json()) as HubAuditEntry[];
          for (const e of entries) {
            if (e.id > sinceRef.current) sinceRef.current = e.id;
            mirrorToClientLog(e);
          }
        }
        // 404/501: endpoint not implemented yet in this hub build — quietly
        // back off rather than spamming the log with errors.
      } catch {
        /* network down, transient — try again next tick */
      } finally {
        if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [token]);
}

function mirrorToClientLog(e: HubAuditEntry): void {
  const data: Record<string, unknown> = {
    hub_id: e.id,
    hub_ts_ms: e.ts_ms,
    actor: e.actor ?? undefined,
    ...(e.data ?? {}),
  };
  const label = `hub · ${e.message}`;
  if (e.level === 'error') log.error('command', label, data);
  else if (e.level === 'warn') log.warn('command', label, data);
  else log.command(label, data);
}
